import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
  ChartData,
} from 'chart.js';
import {
  BarChartMetaData,
  WidgetDefinition,
} from '../../../../../shared/types/widget';
import IndustryStandardsAlert from '../IndustryStandardsAlert';
import signupRateLegend from '../../assets/signup-dataset-legend.svg';
import benchmarkLegend from '../../assets/benchmark-dataset-legend.svg';
import { useFilterData } from '../../../../../utilities/hooks/useFilterData';
import { getIndustryStandardFilterLabel } from '../../utils';
import useFilters from '../../../../../utilities/hooks/useFilters';
import { Typography } from '../../../../widgets/Typography';
import { twMerge } from 'tailwind-merge';
import { useDeviceType } from '../../../../../utilities/common/deviceType';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

interface Props {
  definition: WidgetDefinition;
  content: BarChartMetaData;
}

export default function LineChart({ definition, content }: Readonly<Props>) {
  const isMobile = useDeviceType();
  const { configData } = useFilterData();
  const { benchmarkType } = useFilters();

  const { datasets, target } = content?.data || {};

  const [signupRateDataSet, benchmarkDataSet] = datasets || [];

  const data: ChartData<'line'> = {
    labels: content?.data?.labels || [],
    datasets: [
      {
        label: signupRateDataSet?.label,
        data: signupRateDataSet?.data,
        borderColor: '#0058A8',
        backgroundColor: 'rgba(28, 139, 241, 0.2)',
        borderWidth: 2,
        fill: true,
        tension: 0,
        pointRadius: 3,
        pointBackgroundColor: '#FFF',
        pointBorderColor: '#2563eb',
        pointBorderWidth: 3,
      },
      {
        label: benchmarkDataSet?.label,
        data: benchmarkDataSet?.data,
        borderColor: '#696969',
        backgroundColor: 'transparent',
        borderWidth: 2,
        borderDash: [5, 5],
        fill: false,
        pointRadius: 3,
        pointBackgroundColor: '#FFF',
        pointBorderColor: '#696969',
        pointBorderWidth: 3,
        spanGaps: true,
      },
    ],
  };

  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    aspectRatio: 2.5,
    plugins: {
      legend: {
        display: false,
        position: 'bottom',
        align: 'start',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 20,
          font: {
            size: 12,
          },
          color: '#374151',
          generateLabels: (chart) => {
            const datasets = chart.data.datasets;
            return datasets.map((dataset, i) => ({
              text: '   ' + dataset.label || '',
              fillStyle: '#FFF',
              strokeStyle: dataset.borderColor as string,
              lineWidth: 5,
              hidden: !chart.isDatasetVisible(i),
              datasetIndex: i,
              pointStyle: 'circle',
            }));
          },
        },
      },
      tooltip: {
        enabled: false,
        mode: 'nearest',
        intersect: false,
        callbacks: {
          // Return an empty string so Chart.js skips drawing the numeric value since the plugin enabled globally
          label: () => '',
        },
        external: (context) => {
          const { chart, tooltip } = context;

          let tooltipEl = chart.canvas.parentElement?.querySelector(
            'div.custom-tooltip',
          ) as HTMLDivElement;

          if (!tooltipEl) {
            tooltipEl = document.createElement('div');
            tooltipEl.className = 'custom-tooltip';
            tooltipEl.style.position = 'absolute';
            tooltipEl.style.pointerEvents = 'none';

            tooltipEl.style.transition = 'all .3s ease';
          }

          if (tooltip.opacity === 0) {
            tooltipEl.style.opacity = '0';
            return;
          }

          // Hide tooltip for first data point if it's zero or empty
          const xIndex = tooltip.dataPoints?.[0]?.parsed.x;
          if (xIndex === 0 && !signupRateDataSet.data[xIndex]) {
            tooltipEl.style.opacity = '0';
            return;
          }

          if (tooltip.body) {
            let innerHtml =
              '<div class="bg-white relative rounded-2xl shadow-lg border border-gray-100 p-3 min-w-[400px]">';

            /**
             * Check if the tooltip is out of bounds on the left or right side of the chart
             * If it is, we need to adjust its position accordingly
             */
            const hasLeftOutOfBounds =
              tooltip.caretX - tooltipEl.offsetWidth / 2 < 0;
            const hasRightOutOfBounds =
              tooltip.caretX + tooltipEl.offsetWidth / 2 > chart.width;

            tooltip.dataPoints.forEach((dataPoint: any, i: number) => {
              const label = dataPoint.label;
              const xIndex = dataPoint.parsed.x;

              const currentSignupValue = signupRateDataSet.data[xIndex] ?? 0;
              const currentBenchmarkValue = benchmarkDataSet.data[xIndex] ?? 0;

              const nextSignupValue = signupRateDataSet.data[xIndex + 1];
              const isSignupDataAvailableUntilNextBenchmark =
                nextSignupValue !== null;

              /*
              Determine the month to benchmark against based on data availability
              if signup data is available for the benchmark month, use that month
              otherwise, use the target month from the meta data
              */
              const monthToBenchmarkAgainst =
                isSignupDataAvailableUntilNextBenchmark
                  ? label
                  : `${target.months} months`;

              let isCurrentDataPointAboveBenchmark =
                currentSignupValue > currentBenchmarkValue;

              /*
              If signup data is not available until the next benchmark,
              use the isAboveBenchmark value from the target meta data
              to determine if the current data point is above or below benchmark
              */
              if (!isSignupDataAvailableUntilNextBenchmark) {
                isCurrentDataPointAboveBenchmark = target.isAboveBenchmark;
              }

              chart.canvas.parentNode?.appendChild(tooltipEl);

              /*
              Adjust tooltip position if it goes out of bounds
              if left out of bounds, move right
              if right out of bounds, move left
              */
              if (hasLeftOutOfBounds) {
                tooltipEl.style.transform =
                  'translate(-4rem, calc(-100% - 1rem))';
              } else if (hasRightOutOfBounds) {
                tooltipEl.style.transform =
                  'translate(calc(-100% + 4rem), calc(-100% - 1rem))';
              } else {
                tooltipEl.style.transform =
                  'translate(-50%, calc(-100% - 1rem))';
              }

              innerHtml += '<div class="flex flex-col gap-2">';
              innerHtml += `<span class="text-xs text-left font-figtree font-semibold text-grey-900">${label}</span>`;

              if (currentSignupValue) {
                innerHtml += `<div class="flex flex-row items-center gap-2">`;
                innerHtml += `<img src="${import.meta.env.BASE_URL}charts/line-chart-legend-plan.svg" alt="Signup Rate Legend" />`;
                innerHtml += `<span class="text-xs text-blue-700">Your organisation: ${currentSignupValue}%</span>`;
                innerHtml += `</div>`;
              }

              if (currentBenchmarkValue) {
                innerHtml += `<div class="flex flex-row items-center gap-2">`;
                innerHtml += `<img src="${import.meta.env.BASE_URL}charts/line-chart-legend-dotted.svg" alt="Benchmark Legend" />`;
                innerHtml += `<span class="text-xs text-gray-700">Industry standard (${getIndustryStandardFilterLabel(configData, benchmarkType)}): ${currentBenchmarkValue}%</span>`;
                innerHtml += '</div>';
              }

              innerHtml += `<div class="${twMerge(
                'flex p-2 flex-row border items-center rounded-xl gap-1',
                isCurrentDataPointAboveBenchmark &&
                  'bg-green-25 border-green-100',
                !isCurrentDataPointAboveBenchmark &&
                  'border-yellow-200 bg-yellow-50',
              )}">`;

              if (isCurrentDataPointAboveBenchmark) {
                innerHtml += `<img src="${import.meta.env.BASE_URL}charts/trending-up.svg" aria-hidden="true" />`;
              } else {
                innerHtml += `<img src="${import.meta.env.BASE_URL}charts/trending-down.svg" aria-hidden="true" />`;
              }

              innerHtml += `<span class="text-xs font-figtree text-grey-900">`;
              innerHtml += isCurrentDataPointAboveBenchmark
                ? `Above ${monthToBenchmarkAgainst} industry standard.`
                : `Below ${monthToBenchmarkAgainst} industry standard.`;
              innerHtml += `</span>`;

              innerHtml += `</div>`;
            });

            innerHtml += `<div class="${twMerge(
              'absolute bottom-0 left-1/2 translate-y-1/2 -translate-x-1/2 bg-white h-3 w-3 rounded-xs rotate-45',
              hasLeftOutOfBounds && 'left-14 translate-x-0',
              hasRightOutOfBounds && 'left-auto right-14 translate-x-0',
            )}" />`;

            innerHtml += '</div>';
            tooltipEl.innerHTML = innerHtml;
          }

          const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

          tooltipEl.style.opacity = '1';

          tooltipEl.style.left = positionX + tooltip.caretX + 'px';
          tooltipEl.style.top = positionY + tooltip.caretY + 'px';
        },
      },
      datalabels: {
        display: false,
      },
    },
    scales: {
      x: {
        grid: {
          display: true,
          drawTicks: false,
          color: '#EBEBEB',
          lineWidth: (context) => {
            return context.tick.value === 0 ? 1 : 0;
          },
        },
        border: {
          display: true,
          color: '#e5e7eb',
        },
        ticks: {
          font: {
            size: 11,
          },
          color: '#404040',
          padding: 16,
        },
        title: {
          display: true,
          text: benchmarkDataSet?.label,
          align: 'center',
          font: {
            size: 12,
          },
          color: '#0058A8',
          padding: { top: 10 },
        },
      },
      y: {
        beginAtZero: true,
        max: 100,
        grid: {
          drawTicks: false,
          color: '#EBEBEB',
        },
        border: {
          display: false,
        },
        ticks: {
          stepSize: 25,
          font: {
            size: 11,
          },
          color: '#404040',
          padding: 16,
        },
        title: {
          display: true,
          text: signupRateDataSet?.label,
          align: 'end',
          font: {
            size: 12,
          },
          color: '#0058A8',
          padding: { bottom: 10 },
        },
      },
    },
    interaction: {
      mode: 'index',
      intersect: false,
    },
  };

  if (isMobile) {
    options.maintainAspectRatio = false;
  }

  const legends = [
    {
      label: 'Your organisation sign up rate',
      svg: signupRateLegend,
    },
    {
      label: getIndustryStandardFilterLabel(configData, benchmarkType),
      svg: benchmarkLegend,
    },
  ];

  return (
    <>
      <div className="min-h-[300px] w-full overflow-auto px-6 pt-2 pb-6 md:min-h-[325px]">
        <Line data={data} options={options} />
      </div>
      <div className="mx-6 flex flex-row gap-6">
        {legends.map((legend) => (
          <div key={legend.label} className="flex items-center">
            <img src={legend.svg} alt={legend.label} className="mr-2" />
            <Typography variant="body-sm" weight="medium">
              {legend.label}
            </Typography>
          </div>
        ))}
      </div>
      <div className="m-4">
        <IndustryStandardsAlert
          isAboveBenchmark={target?.isAboveBenchmark}
          aboveBenchmarkLabel={target?.remark}
          belowBenchmarkLabel={target?.remark}
          ctaLabel={target?.link}
        />
      </div>
    </>
  );
}
